/**
 * NeoCoreCampanaInscripcionPage — campaña "solo te faltan los Q100" (insc-nov-2026).
 *
 * Prospectos de agosto a octubre que completaron DPI, selfie y FEIS pero no pagaron la
 * inscripción. Natalie les escribe por WhatsApp desde el número de Neocoop, adjunta a mano los
 * videos desde su teléfono y registra cada paso acá. Un 25 % por segmento es CONTROL (no se les
 * escribe) para medir si la campaña mueve la aguja.
 *
 * Pestañas: Día 1 (apertura) · Día 2 (video CTA) · Conversaciones (plantillas) · Resultados.
 * Backend: campana_insc_crear / _listar / _registrar_contacto / _resultados (migración 0422).
 * Modelo: NeoCoreTestCobranzaPage. Sin datos personales en consola ni en Sentry.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, WhatsappLogo, VideoCamera, ChatCircleDots, Copy, DownloadSimple, Receipt } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { useIsMobile } from '@/hooks/use-mobile'
import { useRole } from '@/lib/useRole'
import { RegistrarInscripcionModal } from '@/components/RegistrarInscripcionModal'

const TEST_ID = 'insc-nov-2026'
const TOPE_DIARIO = 40 // a partir de acá, aviso para no quemar el número

type Paso = 'apertura_enviada' | 'video_confianza_enviado' | 'video_cta_enviado' | 'respondio' | 'no_interesa' | 'no_contesta' | 'numero_malo'

type Row = {
  asociado_id: string
  grupo: 'CONTROL' | 'ENVIO'
  segmento: 'caliente' | 'frio'
  nombre: string
  primer_nombre: string
  mes_inicio: string
  neo_id: string | null
  telefono: string
  genero: string | null
  referidor: string | null
  recibo_vencido: boolean
  estado_actual: string
  convirtio: boolean
  convirtio_at: string | null
  apertura_at: string | null
  respondio_at: string | null
  video_confianza_at: string | null
  video_cta_at: string | null
  excluido: boolean
  ultimo_paso: Paso | null
  ultimo_at: string | null
  ultima_nota: string | null
  contactos: number
}

type Res = {
  grupo: 'CONTROL' | 'ENVIO'; segmento: 'caliente' | 'frio'; personas: number; contactados: number; respondieron: number
  conv_7d: number; conv_14d: number; conv_total: number; pct_7d: number; pct_14d: number; dias_desde_inicio: number
}

type Tab = 'dia1' | 'dia2' | 'conv' | 'res'

const LABEL_PASO: Record<Paso, string> = {
  apertura_enviada: 'Apertura enviada',
  video_confianza_enviado: 'Video confianza enviado',
  video_cta_enviado: 'Video CTA enviado',
  respondio: 'Respondió',
  no_interesa: 'No le interesa',
  no_contesta: 'No contesta',
  numero_malo: 'Número malo',
}

// ── Textos ────────────────────────────────────────────────────────────────────
const fem = (r: Row) => (r.genero ?? '').toUpperCase().startsWith('F')
const activoA = (r: Row) => (fem(r) ? 'activa' : 'activo')

function textoApertura(r: Row): string {
  const ref = r.referidor ? `${r.referidor} te invitó a Neocoop. ` : ''
  return `Hola ${r.primer_nombre}, soy Natalie de Neocoop. ${ref}Vi que en ${r.mes_inicio} empezaste a hacerte ${fem(r) ? 'socia' : 'socio'} desde la app y quedó a medias. ¿Todavía te interesa? Te grabé un video cortito para que sepás quién te escribe.`
}

function textoDia2(r: Row): string {
  return `Hola ${r.primer_nombre}, te dejo otro video cortito con lo único que te falta: el depósito de Q100 (Q50 de inscripción y Q50 de tu aportación, que queda a tu nombre). Con eso quedás ${activoA(r)} y ya podés pedir tu primer crédito desde la app.`
}

type Plantilla = { k: string; l: string; texto: (r: Row) => string; registra?: Paso; pideFecha?: boolean }
const PLANTILLAS: Plantilla[] = [
  {
    k: 'confianza', l: 'Confianza',
    texto: () => 'Te entiendo. Abrí tu app y vas a ver las cuentas: están a nombre de Cooperativa Integral de Ahorro y Crédito NEO, R.L. Esas son las únicas donde se deposita, nunca a una cuenta personal. Somos una cooperativa regulada por INGECOP y nuestra oficina está en El Cubo Center, Ciudad Vieja, Sacatepéquez.',
  },
  {
    k: 'depositar', l: 'Cómo depositar',
    texto: (r) => `Podés depositar o transferir Q100 a cualquiera de estas cuentas, a nombre de Cooperativa Integral de Ahorro y Crédito NEO, R.L.: BAC monetaria 907312102 · Interbanco ahorro 7101905269. En la referencia poné tu código ${r.neo_id ?? ''}. Después mandame por aquí la foto de la boleta y yo registro tu pago.`
      + (r.recibo_vencido ? ' Mandame también la foto de un recibo de luz o agua de este mes o del anterior.' : ''),
  },
  {
    k: 'sin100', l: 'No tengo los Q100', pideFecha: true,
    texto: () => 'Sin problema, sin presión. ¿Qué fecha te queda bien? Te escribo ese día y lo hacemos juntos.',
  },
  {
    k: 'cuanto', l: 'Cuánto me prestan',
    texto: (r) => `Tu primer crédito es de Q500 y va subiendo a medida que pagás a tiempo y sumás a alguien. Lo pedís desde la app apenas quedás ${activoA(r)}.`,
  },
  {
    k: 'yano', l: 'Ya no lo necesito', registra: 'no_interesa',
    texto: () => 'Gracias por contarme. No te vuelvo a escribir.',
  },
]

const waLink = (tel: string, txt?: string) => {
  const num = tel.replace(/\D/g, '')
  return `https://wa.me/${num}${txt ? `?text=${encodeURIComponent(txt)}` : ''}`
}
const hoyGTstr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
const diaGT = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' })
const fechaCorta = (iso: string) =>
  new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
const UN_DIA = 24 * 3600 * 1000

function csv(rows: Row[]): string {
  const cols: (keyof Row)[] = ['nombre', 'primer_nombre', 'neo_id', 'telefono', 'grupo', 'segmento', 'mes_inicio', 'referidor', 'recibo_vencido', 'estado_actual', 'convirtio', 'apertura_at', 'respondio_at', 'video_confianza_at', 'video_cta_at', 'ultimo_paso', 'ultima_nota']
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
  return [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n')
}

export function NeoCoreCampanaInscripcionPage() {
  const isMobile = useIsMobile()
  const { isAdmin } = useRole()
  const [rows, setRows] = useState<Row[]>([])
  const [res, setRes] = useState<Res[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('dia1')
  const [busy, setBusy] = useState<string | null>(null)
  const [abierta, setAbierta] = useState<string | null>(null) // fila de Conversaciones expandida
  const [fecha, setFecha] = useState('')
  const [inscribir, setInscribir] = useState<Row | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [l, r] = await Promise.all([
      supabase.rpc('campana_insc_listar', { p_test: TEST_ID }),
      supabase.rpc('campana_insc_resultados', { p_test: TEST_ID }),
    ])
    if (l.error) alert('No se pudo cargar la campaña: ' + l.error.message)
    setRows((l.data as Row[]) ?? [])
    setRes((r.data as Res[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  // Registra uno o varios pasos del mismo toque (p. ej. apertura + video de confianza, que Natalie
  // adjunta en el mismo chat). Se recarga una sola vez al final.
  const registrar = async (r: Row, pasos: Paso | Paso[], nota?: string) => {
    const lista = Array.isArray(pasos) ? pasos : [pasos]
    setBusy(r.asociado_id + lista[0])
    for (const paso of lista) {
      const { error } = await supabase.rpc('campana_insc_registrar_contacto', {
        p_asociado_id: r.asociado_id, p_paso: paso, p_nota: nota ?? null, p_test: TEST_ID,
      })
      if (error) { setBusy(null); alert('Error: ' + error.message); void load(); return false }
    }
    setBusy(null)
    void load()
    return true
  }

  const deshacer = async (r: Row) => {
    if (!confirm(`¿Deshacer el último paso de ${r.primer_nombre} (${r.ultimo_paso ? LABEL_PASO[r.ultimo_paso] : ''})?`)) return
    const { error } = await supabase.rpc('campana_insc_deshacer_ultimo', { p_asociado_id: r.asociado_id, p_test: TEST_ID })
    if (error) { alert('Error: ' + error.message); return }
    void load()
  }

  const crear = async () => {
    if (!confirm('¿Crear la campaña? Congela el listado de prospectos de agosto a octubre y sortea el grupo control (25 %). Se hace una sola vez.')) return
    const { data, error } = await supabase.rpc('campana_insc_crear', { p_test: TEST_ID })
    if (error) { alert('Error: ' + error.message); return }
    const d = data as { total: number; control: number; envio: number }
    alert(`Campaña lista: ${d.total} personas · ${d.envio} para escribir · ${d.control} control.`)
    void load()
  }

  const envio = useMemo(() => rows.filter((r) => r.grupo === 'ENVIO' && !r.excluido && !r.convirtio), [rows])
  const listas: Record<Exclude<Tab, 'res'>, Row[]> = useMemo(() => {
    const ahora = Date.now()
    // Calientes primero: responden más y no queman el número con fríos que no contestan.
    const orden = (a: Row, b: Row) => (a.segmento === b.segmento ? 0 : a.segmento === 'caliente' ? -1 : 1)
    return {
      dia1: envio.filter((r) => !r.apertura_at).sort(orden),
      dia2: envio.filter((r) => r.apertura_at && ahora - new Date(r.apertura_at).getTime() >= UN_DIA && !r.respondio_at && !r.video_cta_at).sort(orden),
      conv: rows.filter((r) => r.grupo === 'ENVIO' && !r.excluido && r.respondio_at && !r.convirtio),
    }
  }, [envio, rows])

  const enviadosHoy = useMemo(() => rows.filter((r) => r.apertura_at && diaGT(r.apertura_at) === hoyGTstr()).length, [rows])
  const registradosHoy = useMemo(
    () => rows.filter((r) => r.ultimo_at && diaGT(r.ultimo_at) === hoyGTstr()).sort((a, b) => (b.ultimo_at! > a.ultimo_at! ? 1 : -1)),
    [rows])
  const convertidos = rows.filter((r) => r.grupo === 'ENVIO' && r.convirtio).length

  const descargar = () => {
    const lista = tab === 'res' ? rows : listas[tab]
    const blob = new Blob(['﻿' + csv(lista)], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `campana-inscripcion-${tab}-${hoyGTstr()}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const copiar = async (t: string) => {
    try { await navigator.clipboard.writeText(t) } catch { prompt('Copiá el texto:', t) }
  }

  // ── UI helpers ──────────────────────────────────────────────────────────────
  const chip = (t: string, bg: string, fg: string) => (
    <span style={{ background: bg, color: fg, padding: '2px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap' }}>{t}</span>
  )
  const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', minHeight: isMobile ? 40 : 32, justifyContent: 'center', flex: isMobile ? 1 : undefined }
  const pasoBtn = (r: Row, paso: Paso, label: string, Icon?: typeof VideoCamera) => (
    <button key={paso} className="btn btn-ghost" style={btn} disabled={busy === r.asociado_id + paso}
      onClick={() => {
        if ((paso === 'no_interesa' || paso === 'numero_malo') && !confirm(`¿Marcar "${LABEL_PASO[paso]}"? Sale del listado para siempre.`)) return
        void registrar(r, paso)
      }}>
      {Icon && <Icon size={14} />} {label}
    </button>
  )

  const fila = (r: Row) => {
    const enTab = tab as Exclude<Tab, 'res'>
    const texto = enTab === 'dia1' ? textoApertura(r) : enTab === 'dia2' ? textoDia2(r) : undefined
    return (
      <div key={r.asociado_id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--card)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <a href={`/neocore/asociados/${r.asociado_id}`} style={{ fontWeight: 700, fontSize: 14.5 }}>{r.nombre}</a>
            <div style={{ color: 'var(--muted-foreground)', fontSize: 12 }}>#{r.neo_id} · empezó en {r.mes_inicio}</div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {r.segmento === 'caliente' ? chip('Caliente', 'rgba(234,88,12,.12)', '#c2410c') : chip('Frío', 'rgba(100,116,139,.12)', '#475569')}
            {r.recibo_vencido && chip('Recibo vencido', 'rgba(220,38,38,.08)', '#b91c1c')}
            {r.referidor && chip(`Referido de ${r.referidor}`, 'rgba(22,163,74,.10)', '#15803d')}
          </div>
        </div>

        {texto && <p style={{ fontSize: 12.5, margin: '8px 0', color: 'var(--muted-foreground)', lineHeight: 1.45 }}>{texto}</p>}
        {r.ultimo_paso && (
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            {LABEL_PASO[r.ultimo_paso]} · {fechaCorta(r.ultimo_at!)}{r.ultima_nota ? <> · <i>{r.ultima_nota}</i></> : null}
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* Día 1 y 2: un solo botón. Abre el chat con el texto listo; Natalie adjunta el video en ese
              mismo chat y envía. Queda registrado texto + video juntos. */}
          <a className="btn btn-ghost" style={{ ...btn, fontWeight: 700 }} href={waLink(r.telefono, texto)} target="_blank" rel="noreferrer"
            onClick={() => {
              if (enTab === 'dia1') void registrar(r, ['apertura_enviada', 'video_confianza_enviado'])
              if (enTab === 'dia2') void registrar(r, 'video_cta_enviado')
            }}>
            <WhatsappLogo size={15} />
            {enTab === 'dia1' ? ' Enviar texto + video confianza' : enTab === 'dia2' ? ' Enviar texto + video CTA' : ' WhatsApp'}
            {enTab !== 'conv' && <VideoCamera size={14} />}
          </a>
          {enTab !== 'conv' && pasoBtn(r, 'respondio', 'Respondió', ChatCircleDots)}
          {enTab === 'conv' && (
            <button className="btn btn-ghost" style={btn} onClick={() => { setAbierta(abierta === r.asociado_id ? null : r.asociado_id); setFecha('') }}>
              <Copy size={14} /> Plantillas
            </button>
          )}
          {enTab === 'conv' && (
            <button className="btn btn-ghost" style={btn} onClick={() => setInscribir(r)}>
              <Receipt size={14} /> Registrar pago
            </button>
          )}
          {pasoBtn(r, 'no_contesta', 'No contesta')}
          {pasoBtn(r, 'numero_malo', 'Número malo')}
          {pasoBtn(r, 'no_interesa', 'No le interesa')}
        </div>

        {enTab === 'conv' && abierta === r.asociado_id && (
          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
            {PLANTILLAS.map((p) => {
              const t = p.texto(r)
              return (
                <div key={p.k} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 4 }}>{p.l}</div>
                  <div style={{ fontSize: 12.5, lineHeight: 1.45 }}>{t}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button className="btn btn-ghost" style={btn} onClick={() => void copiar(t)}><Copy size={14} /> Copiar</button>
                    <a className="btn btn-ghost" style={btn} href={waLink(r.telefono, t)} target="_blank" rel="noreferrer"
                      onClick={() => { if (p.registra) void registrar(r, p.registra) }}>
                      <WhatsappLogo size={14} /> Enviar
                    </a>
                    {p.pideFecha && (
                      <>
                        <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={{ minHeight: 32 }} />
                        <button className="btn btn-ghost" style={btn} disabled={!fecha}
                          onClick={() => void registrar(r, 'respondio', `Va a pagar el ${fecha}`)}>Guardar fecha</button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const tabs: { k: Tab; l: string; n?: number; d: string }[] = [
    { k: 'dia1', l: 'Día 1', n: listas.dia1.length, d: 'Tocá "Enviar texto + video confianza": se abre el chat con el texto listo. Adjuntá el video de confianza en ese mismo chat y enviá. Queda registrado solo. Si tocaste por error, usá "Deshacer" en "Registrados hoy".' },
    { k: 'dia2', l: 'Día 2', n: listas.dia2.length, d: 'Les escribiste hace un día o más y no respondieron. Tocá "Enviar texto + video CTA", adjuntá el video CTA en el mismo chat y enviá.' },
    { k: 'conv', l: 'Conversaciones', n: listas.conv.length, d: 'Respondieron y todavía no pagaron. Usá las plantillas. Cuando te manden la boleta, "Registrar pago".' },
    { k: 'res', l: 'Resultados', d: 'Se mide solo: cuántos pagaron la inscripción, comparando a quienes les escribimos contra el control.' },
  ]
  const tabActual = tabs.find((t) => t.k === tab)!
  const sinCampana = !loading && rows.length === 0

  return (
    <div style={{ padding: isMobile ? '14px 12px' : '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: isMobile ? 19 : 22, fontWeight: 800, letterSpacing: '-0.01em' }}>Campaña inscripción · solo faltan los Q100</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Prospectos de agosto a octubre. Registrá cada paso: sin registro no sabemos qué funcionó.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {isAdmin && rows.length > 0 && (
            <button className="btn btn-ghost" style={{ ...btn, flex: undefined }} onClick={descargar} title="Descargar CSV del listado visible">
              <DownloadSimple size={14} />{!isMobile && ' CSV'}
            </button>
          )}
          <button className="btn btn-ghost" style={{ ...btn, flex: undefined }} onClick={() => void load()} title="Refrescar">
            <ArrowsClockwise size={14} weight="bold" />{!isMobile && ' Refrescar'}
          </button>
        </div>
      </div>

      {sinCampana ? (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 20, background: 'var(--card)' }}>
          <p style={{ margin: '0 0 12px' }}>La campaña todavía no está creada.</p>
          {isAdmin ? <button className="btn btn-primary" onClick={() => void crear()}>Crear campaña</button>
            : <p style={{ color: 'var(--muted-foreground)', margin: 0 }}>Pedile a Hugo que la cree.</p>}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: enviadosHoy >= TOPE_DIARIO ? 'rgba(220,38,38,.06)' : 'var(--card)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted-foreground)' }}>Enviados hoy</div>
              <div style={{ fontSize: 26, fontWeight: 800 }}>{enviadosHoy}<span style={{ fontSize: 14, color: 'var(--muted-foreground)' }}> / {TOPE_DIARIO}</span></div>
              {enviadosHoy >= TOPE_DIARIO && <div style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>Pará por hoy para no quemar el número.</div>}
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--card)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted-foreground)' }}>Pagaron (a quienes escribimos)</div>
              <div style={{ fontSize: 26, fontWeight: 800 }}>{convertidos}</div>
            </div>
            {!isMobile && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: 'var(--card)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted-foreground)' }}>Por escribir</div>
                <div style={{ fontSize: 26, fontWeight: 800 }}>{listas.dia1.length}</div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, padding: 4, background: 'var(--muted, #f4f4f5)', borderRadius: 10, marginBottom: 10 }}>
            {tabs.map((t) => (
              <button key={t.k} onClick={() => { setTab(t.k); setAbierta(null) }}
                style={{ border: 0, borderRadius: 8, padding: '8px 4px', fontSize: isMobile ? 12.5 : 13.5, fontWeight: tab === t.k ? 700 : 500,
                  background: tab === t.k ? 'var(--card, #fff)' : 'transparent', boxShadow: tab === t.k ? '0 1px 3px rgba(0,0,0,.12)' : 'none' }}>
                {t.l}{t.n !== undefined ? ` · ${t.n}` : ''}
              </button>
            ))}
          </div>
          <p style={{ fontSize: 13, margin: '0 0 12px' }}>{tabActual.d}</p>

          {tab !== 'res' && registradosHoy.length > 0 && (
            <details style={{ margin: '0 0 12px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Registrados hoy · {registradosHoy.length} (deshacer si fue por error)</summary>
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                {registradosHoy.map((r) => (
                  <div key={r.asociado_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span>{r.nombre} · <span style={{ color: 'var(--muted-foreground)' }}>{r.ultimo_paso ? LABEL_PASO[r.ultimo_paso] : ''} · {fechaCorta(r.ultimo_at!)}</span></span>
                    <button className="btn btn-ghost" style={{ border: '1px solid var(--border)' }} onClick={() => void deshacer(r)}>Deshacer</button>
                  </div>
                ))}
              </div>
            </details>
          )}

          {loading ? <p>Cargando…</p> : tab === 'res' ? (
            <Resultados res={res} />
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {listas[tab].length === 0 ? <p style={{ color: 'var(--muted-foreground)' }}>Nada pendiente en esta pestaña.</p> : listas[tab].map(fila)}
            </div>
          )}
        </>
      )}

      {inscribir && (
        <RegistrarInscripcionModal
          open
          onClose={() => setInscribir(null)}
          asociadoId={inscribir.asociado_id}
          nombreCompleto={inscribir.nombre}
          noAsociado={inscribir.neo_id ?? ''}
          neoId={inscribir.neo_id ?? undefined}
          onSuccess={() => { setInscribir(null); void load() }}
        />
      )}
    </div>
  )
}

function Resultados({ res }: { res: Res[] }) {
  const dias = res[0]?.dias_desde_inicio ?? 0
  const celda = (g: Res['grupo'], s: Res['segmento']) => res.find((r) => r.grupo === g && r.segmento === s)
  const total = (g: Res['grupo']) => {
    const xs = res.filter((r) => r.grupo === g)
    const p = xs.reduce((a, r) => a + r.personas, 0)
    const c7 = xs.reduce((a, r) => a + r.conv_7d, 0)
    const c14 = xs.reduce((a, r) => a + r.conv_14d, 0)
    return { p, c7, c14, pct7: p ? (100 * c7) / p : 0, pct14: p ? (100 * c14) / p : 0 }
  }
  const te = total('ENVIO'), tc = total('CONTROL')
  const th: React.CSSProperties = { padding: 8, textAlign: 'left', fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 600 }
  const td: React.CSSProperties = { padding: 8, fontVariantNumeric: 'tabular-nums' }
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--card)' }}>
        <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Día {dias} de la campaña · pagaron la inscripción a los 14 días</div>
        <div style={{ display: 'flex', gap: 24, marginTop: 6, flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: 28, fontWeight: 800 }}>{te.pct14.toFixed(1)}%</div><div style={{ fontSize: 12 }}>les escribimos ({te.c14} de {te.p})</div></div>
          <div><div style={{ fontSize: 28, fontWeight: 800, color: 'var(--muted-foreground)' }}>{tc.pct14.toFixed(1)}%</div><div style={{ fontSize: 12 }}>control ({tc.c14} de {tc.p})</div></div>
          <div><div style={{ fontSize: 28, fontWeight: 800 }}>{(te.pct14 - tc.pct14 >= 0 ? '+' : '') + (te.pct14 - tc.pct14).toFixed(1)} pts</div><div style={{ fontSize: 12 }}>efecto de la campaña</div></div>
        </div>
        {dias < 14 && <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 6 }}>La medición de 14 días todavía está corriendo.</div>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={th}>Segmento</th><th style={th}>Grupo</th><th style={th}>Personas</th><th style={th}>Escritos</th>
              <th style={th}>Respondieron</th><th style={th}>Pagaron 7 d</th><th style={th}>Pagaron 14 d</th>
            </tr>
          </thead>
          <tbody>
            {(['caliente', 'frio'] as const).flatMap((s) => (['ENVIO', 'CONTROL'] as const).map((g) => {
              const x = celda(g, s)
              return (
                <tr key={s + g} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={td}>{s === 'caliente' ? 'Caliente' : 'Frío'}</td>
                  <td style={td}>{g === 'ENVIO' ? 'Les escribimos' : 'Control'}</td>
                  <td style={td}>{x?.personas ?? 0}</td>
                  <td style={td}>{g === 'CONTROL' ? '—' : x?.contactados ?? 0}</td>
                  <td style={td}>{g === 'CONTROL' ? '—' : x?.respondieron ?? 0}</td>
                  <td style={td}>{x?.conv_7d ?? 0} ({Number(x?.pct_7d ?? 0).toFixed(1)}%)</td>
                  <td style={td}>{x?.conv_14d ?? 0} ({Number(x?.pct_14d ?? 0).toFixed(1)}%)</td>
                </tr>
              )
            }))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
